import React, { useEffect } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeCookie = 'indigo-toggle-dark';
let themeCookieExpiry = 90; // days

const AddDarkTheme = () => {
  const cookies = new Cookies();
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + themeCookieExpiry);
  };

  const getCookieOptions = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const options = { domain: serverURL.hostname, path: '/', expires: getCookieExpiry() };
    return options;
  };

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[index].contentDocument) { iframes[index].contentDocument.head.appendChild(style); }
      });
    }
  };

  useEffect(() => {
    const theme = cookies.get(themeCookie);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.body.classList.add('indigo-dark-theme');
      
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage

      cookies.set(themeCookie, theme, getCookieOptions());      //  on page load, update expiry
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');const { Popover } = await import('@typeform/embed-react');
const { getAuthenticatedHttpClient } = await import('@edx/frontend-platform/auth');
const { useState, useEffect } = await import('react');

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    width: '350px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  text: {
    fontSize: '14px',
    marginBottom: '20px',
    color: '#333',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  buttonPrimary: {
    backgroundColor: '#d9534f',
    color: '#fff',
    border: 'none',
    padding: '10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  buttonSecondary: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    padding: '10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
};

const Modal = ({ isModalOpen, handleOnClick }) => {
  const getDialogStyles = () => ({ ...styles.overlay, visibility: isModalOpen ? 'visible' : 'hidden' })

  return (
    <dialog open={isModalOpen} style={getDialogStyles()}>
      <div style={styles.modal}>
        <div style={styles.title}>Skip This Form?</div>
        <div style={styles.text}>
          Would you like to skip this form permanently, or would you like to fill it out later?
        </div>
        <div style={styles.buttonContainer}>
          <button style={styles.buttonPrimary} onClick={() => handleOnClick(true)}>
            Skip permanently
          </button>
          <button style={styles.buttonSecondary} onClick={() => handleOnClick(false)}>
            Fill out later
          </button>
        </div>
      </div>
    </dialog>
  );
};

/*
Display an onboarding form. This form is displayed just once: after it is submitted, we
make a POST call to the Edly API endpoint  to tell the backend not to display the form
again. In addition, making a GET to this same endpoint tells the frontend whether to
display the form. A negative response is stored in local storage, to minimize API calls.
*/


const EdlyOnboardingSurvey = () => {
  const [hasFormLoadedOnce, setHasFormLoadedOnce] = useState(false);
  const [apiData, setApiData] = useState({
    name: '',
    email: '',
    showForm: null,
    autoLoadForm: null
  });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // See the corresponding url and view in the Edly self-hosted app.
  const apiUrl = `${getConfig().LMS_BASE_URL}/edly/api/onboarding-survey/`;
  const localStorageKey = "edly_onboarding_form_filled";

  const updateOnboardingFormStatus = async () => {
    await getAuthenticatedHttpClient().post(apiUrl, {
      form_filled: true,
      auto_load_form: false
    })
    localStorage.setItem(localStorageKey, "true");
    window.location.reload();
  }

  const updateLoadFormStatus = async () => {
    await getAuthenticatedHttpClient().post(apiUrl, {
      auto_load_form: false,
      form_filled: false
    })
  }

  const handleOnSubmit = () => updateOnboardingFormStatus();

  const handleOnClick = async (hideForever) => {
    setIsModalOpen(false);

    if (hideForever) {
      updateOnboardingFormStatus();
    } else {
      updateLoadFormStatus();
    }
  }

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await getAuthenticatedHttpClient().get(apiUrl);
        setApiData({
          name: response.data.username,
          email: response.data.email,
          showForm: response.data.show_form,
          autoLoadForm: response.data.auto_load_form
        })

        if (!response.data.show_form) {
          // Cache negative responses
          localStorage.setItem(localStorageKey, "true");
        }
      } catch {
        setApiData((data) => ({
          ...data,
          showForm: false
        }))
      } finally {
        setLoading(false);
      }
    };

    // Check API when cache is not defined
    if (!localStorage.getItem(localStorageKey)) fetchFormData();
  }, []);

  if (loading) return <></>;

  // https://www.typeform.com/developers/embed/react/
  return apiData.showForm 
  ? <>
      <Modal isModalOpen={isModalOpen} handleOnClick={handleOnClick} />
      <Popover 
        id="WHBX8vDV" 
        height="90vh" 
        width="50vw" 
        open="load" 
        hidden={apiData} 
        onSubmit={handleOnSubmit} 
        onClose={() => {
          setIsModalOpen(true);
          setHasFormLoadedOnce(true);
        }}
        preventReopenOnClose={hasFormLoadedOnce || !apiData.autoLoadForm}
      /> 
  </>
  : <></>;
}

const { default: IndigoFooter } = await import('@edly-io/indigo-frontend-component-footer');
    addPlugins(config, 'footer_slot', [{
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
        id: 'custom_footer',
        type: DIRECT_PLUGIN,
        RenderWidget: () => <EdlyOnboardingSurvey />
    }
}]);
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'footer_slot', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'footer_slot', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'footer_slot', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'footer_slot', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'footer_slot', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
    }
  } catch { }

  return config;
}

export default setConfig;